import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Global modal orchestration.
 *
 * Any component (header button, page CTA, keyboard shortcut) can open one of
 * the four global modals without owning its state. The Layout mounts a single
 * instance of each modal and subscribes to this context.
 *
 * Supported modals:
 *   - trade       (TradeModal)
 *   - dividend    (DividendModal)
 *   - deposit     (DepositModal)
 *   - calculator  (CompoundInterestModal)
 *
 * Each opener accepts an optional `payload` (e.g. an existing record to edit).
 */

const ModalContext = createContext(null);

export function useModals() {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModals must be used within ModalProvider');
  }
  return ctx;
}

export function ModalProvider({ children }) {
  // activeModal: 'trade' | 'dividend' | 'deposit' | 'calculator' | null
  const [activeModal, setActiveModal] = useState(null);
  // payload: optional data for the modal (e.g. record to edit). null for create.
  const [payload, setPayload] = useState(null);

  const openTrade = useCallback((trade = null) => {
    setPayload(trade);
    setActiveModal('trade');
  }, []);

  const openDividend = useCallback((dividend = null) => {
    setPayload(dividend);
    setActiveModal('dividend');
  }, []);

  const openDeposit = useCallback((deposit = null) => {
    setPayload(deposit);
    setActiveModal('deposit');
  }, []);

  const openCalculator = useCallback(() => {
    setPayload(null);
    setActiveModal('calculator');
  }, []);

  const closeAll = useCallback(() => {
    setActiveModal(null);
    setPayload(null);
  }, []);

  const value = useMemo(
    () => ({
      activeModal,
      payload,
      openTrade,
      openDividend,
      openDeposit,
      openCalculator,
      closeAll,
      isOpen: (name) => activeModal === name,
    }),
    [activeModal, payload, openTrade, openDividend, openDeposit, openCalculator, closeAll],
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}
